import type { AppUser, Task, WorkRequest } from '../types';
import { isTaskClosed, isTaskOverdue } from './calculations';

interface WithId<T> { id: string; data: T }

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

function millis(value: { toMillis(): number } | null | undefined): number | null {
  return value && typeof value.toMillis === 'function' ? value.toMillis() : null;
}

function percent(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

function isCompleted(task: WithId<Task>): boolean {
  return ['completed', 'archived'].includes(task.data.status);
}

export interface EmployeeMetrics {
  assigned: number;
  open: number;
  overdue: number;
  completed: number;
  completionRate: number;
  commitmentRate: number;
  averageDelayDays: number;
  delayControl: number;
  averageResponseHours: number;
  responseSpeed: number;
  overallScore: number;
  lastActivityAt: Date | null;
}

export function employeeMetrics(uid: string, tasks: WithId<Task>[], requests: WithId<WorkRequest>[], now = new Date()): EmployeeMetrics {
  const assigned = tasks.filter((task) => task.data.assigneeIds.includes(uid));
  const completed = assigned.filter(isCompleted);
  const open = assigned.filter((task) => !isTaskClosed(task.data.status));
  const overdue = open.filter((task) => isTaskOverdue(task.data, now));
  const onTime = completed.filter((task) => {
    const decided = millis(task.data.approval.decidedAt);
    return decided !== null && decided <= task.data.currentDueAt.toMillis();
  });
  const delayDays = completed.map((task) => {
    const decided = millis(task.data.approval.decidedAt) ?? task.data.updatedAt.toMillis();
    return Math.max(0, (decided - task.data.currentDueAt.toMillis()) / DAY_MS);
  });
  const averageDelayDays = delayDays.length ? Math.round((delayDays.reduce((sum, value) => sum + value, 0) / delayDays.length) * 10) / 10 : 0;
  const received = requests.filter((request) => request.data.recipientId === uid && request.data.firstDecisionAt);
  const responseHours = received.map((request) => Math.max(0, ((millis(request.data.firstDecisionAt) ?? request.data.updatedAt.toMillis()) - request.data.createdAt.toMillis()) / HOUR_MS));
  const averageResponseHours = responseHours.length ? Math.round((responseHours.reduce((sum, value) => sum + value, 0) / responseHours.length) * 10) / 10 : 0;
  const completionRate = percent(completed.length, assigned.length);
  const commitmentRate = percent(onTime.length, completed.length);
  const delayControl = Math.max(0, Math.round(100 - averageDelayDays * 10));
  const responseSpeed = received.length ? Math.max(0, Math.round(100 - (averageResponseHours / 72) * 100)) : 100;
  const overallScore = Math.round((completionRate + commitmentRate + delayControl + responseSpeed) / 4);
  const activityTimes = [
    ...assigned.map((task) => task.data.updatedAt?.toMillis?.() ?? 0),
    ...requests.filter((request) => request.data.requesterId === uid || request.data.recipientId === uid).map((request) => request.data.updatedAt?.toMillis?.() ?? 0),
  ].filter(Boolean);
  return { assigned: assigned.length, open: open.length, overdue: overdue.length, completed: completed.length, completionRate, commitmentRate, averageDelayDays, delayControl, averageResponseHours, responseSpeed, overallScore, lastActivityAt: activityTimes.length ? new Date(Math.max(...activityTimes)) : null };
}

export interface OrganizationMetrics {
  organizationId: string;
  tasks: number;
  completed: number;
  overdue: number;
  commitmentRate: number;
  averageDelayDays: number;
}

export function organizationMetrics(tasks: WithId<Task>[], now = new Date()): OrganizationMetrics[] {
  const ids = [...new Set(tasks.map((task) => task.data.organizationId || 'غير محدد'))];
  return ids.map((organizationId) => {
    const scoped = tasks.filter((task) => (task.data.organizationId || 'غير محدد') === organizationId);
    const completed = scoped.filter(isCompleted);
    const overdue = scoped.filter((task) => isTaskOverdue(task.data, now));
    const onTime = completed.filter((task) => (millis(task.data.approval.decidedAt) ?? Infinity) <= task.data.currentDueAt.toMillis());
    const delay = completed.map((task) => Math.max(0, ((millis(task.data.approval.decidedAt) ?? task.data.updatedAt.toMillis()) - task.data.currentDueAt.toMillis()) / DAY_MS));
    return { organizationId, tasks: scoped.length, completed: completed.length, overdue: overdue.length, commitmentRate: percent(onTime.length, completed.length), averageDelayDays: delay.length ? Math.round((delay.reduce((sum, value) => sum + value, 0) / delay.length) * 10) / 10 : 0 };
  }).sort((a, b) => b.tasks - a.tasks);
}

export function overallReport(tasks: WithId<Task>[], requests: WithId<WorkRequest>[], now = new Date()) {
  const completed = tasks.filter(isCompleted);
  const onTime = completed.filter((task) => (millis(task.data.approval.decidedAt) ?? Infinity) <= task.data.currentDueAt.toMillis());
  const delay = completed.map((task) => Math.max(0, ((millis(task.data.approval.decidedAt) ?? task.data.updatedAt.toMillis()) - task.data.currentDueAt.toMillis()) / DAY_MS));
  const decided = requests.filter((request) => request.data.firstDecisionAt);
  const responseHours = decided.map((request) => Math.max(0, ((millis(request.data.firstDecisionAt) ?? request.data.updatedAt.toMillis()) - request.data.createdAt.toMillis()) / HOUR_MS));
  return {
    commitmentRate: percent(onTime.length, completed.length),
    averageDelayDays: delay.length ? Math.round((delay.reduce((sum, value) => sum + value, 0) / delay.length) * 10) / 10 : 0,
    averageResponseHours: responseHours.length ? Math.round((responseHours.reduce((sum, value) => sum + value, 0) / responseHours.length) * 10) / 10 : 0,
    completionRate: percent(completed.length, tasks.length),
    organizations: organizationMetrics(tasks, now),
  };
}

export function requestMatrix(users: WithId<Pick<AppUser, 'name' | 'status'>>[], requests: WithId<WorkRequest>[], now = new Date()) {
  const activeUsers = users.filter((user) => user.data.status === 'active');
  const cells = new Map<string, { total: number; pending: number; overdue: number }>();
  for (const request of requests) {
    const key = `${request.data.requesterId}::${request.data.recipientId}`;
    const current = cells.get(key) ?? { total: 0, pending: 0, overdue: 0 };
    current.total += 1;
    if (['pending', 'clarification_requested'].includes(request.data.status)) {
      current.pending += 1;
      if (request.data.dueAt.toMillis() < now.getTime()) current.overdue += 1;
    }
    cells.set(key, current);
  }
  return { users: activeUsers, cells };
}
